import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Grid,
    Row,
    Col,
    Table,
    Button,
    Modal,
    Form,
    FormControl,
    FormGroup,
    ControlLabel,
    Pagination,
    InputGroup,
    Glyphicon,
    Badge,
    DropdownButton,
    MenuItem
} from 'react-bootstrap';
import Card from '../../../components/Card/Card.jsx';
import { fetchAllMongoProducts } from '../../../redux/actions/product';
import Papa from 'papaparse';
import './DatabaseManagement.css';

class DatabaseManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            products: [],
            filteredProducts: [],
            loading: true,
            currentPage: 1,
            itemsPerPage: 50,
            itemsPerPageOptions: [25, 50, 100, 200],
            totalPages: 0,
            showModal: false,
            modalType: 'view',
            selectedProduct: null,
            sortConfig: {
                key: 'nomCommercial',
                direction: 'ascending'
            },
            filters: {
                famille: '',
                nomCommercial: '',
                dci: '',
                dosage: '',
                forme: '',
                conditionnement: '',
                search: '',
                reference: '' // 'all', 'referenced', 'non-referenced'
            },
            editForm: {
                nom: '',
                nomCommercial: '',
                DCI: '',
                dosage: '',
                forme: '',
                conditionnement: '',
                famille: '',
                laboratoire: '',
                prix: '',
                code: '',
                isRef: 0,
                referenceProductId: ''
            },
            stats: {
                total: 0,
                filtered: 0
            }
        };
    }

    componentDidMount() {
        this.loadProducts();
    }

    loadProducts = async () => {
        try {
            this.setState({ loading: true, error: null });

            let products = await this.props.fetchAllMongoProducts();
            products = Array.isArray(products) ? products : [];

            this.setState({
                products: products,
                filteredProducts: products,
                loading: false,
                totalPages: Math.ceil(products.length / this.state.itemsPerPage),
                stats: {
                    total: products.length,
                    filtered: products.length
                }
            });
        } catch (error) {
            this.setState({
                loading: false,
                error: error.message || 'Erreur lors du chargement des données.'
            });
            if (this.props.showNotification) {
                this.props.showNotification(
                    error.message || 'Erreur lors du chargement des données',
                    'error'
                );
            }
        }
    }

    handleSort = (key) => {
        let direction = 'ascending';
        if (this.state.sortConfig.key === key && this.state.sortConfig.direction === 'ascending') {
            direction = 'descending';
        }

        const sortedProducts = [...this.state.filteredProducts].sort((a, b) => {
            const aValue = a[key] || '';
            const bValue = b[key] || '';

            if (aValue < bValue) {
                return direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });

        this.setState({
            sortConfig: { key, direction },
            filteredProducts: sortedProducts,
            currentPage: 1
        });
    }

    getSortIndicator = (key) => {
        const { sortConfig } = this.state;
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
        }
        return '';
    }

    handleFilterChange = (filterName, value) => {
        const newFilters = {
            ...this.state.filters,
            [filterName]: value
        };

        this.setState({ filters: newFilters, currentPage: 1 }, () => {
            this.applyFilters();
        });
    }

    applyFilters = () => {
        const { products, filters } = this.state;
        let filtered = products;

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(product => {
                const fields = [
                    product.nom || '',
                    product.nomCommercial || '',
                    product.DCI || product.dci || '',
                    product.laboratoire || product.fabricant || '',
                    product.forme || '',
                    product.famille || '',
                    product.dosage || '',
                    product.conditionnement || '',
                    product.code || ''
                ];

                return fields.some(field => field.toLowerCase().includes(searchTerm));
            });
        }

        // Filtre par référence
        if (filters.reference) {
            if (filters.reference === 'referenced') {
                filtered = filtered.filter(product => product.isRef === 1);
            } else if (filters.reference === 'non-referenced') {
                filtered = filtered.filter(product => product.isRef === 0 || product.isRef === undefined || product.isRef === null);
            }
        }

        Object.keys(filters).forEach(key => {
            if (key !== 'search' && key !== 'reference' && filters[key]) {
                filtered = filtered.filter(product => {
                    const productValue = product[key] || '';
                    return productValue.toLowerCase().includes(filters[key].toLowerCase());
                });
            }
        });

        this.setState({
            filteredProducts: filtered,
            totalPages: Math.ceil(filtered.length / this.state.itemsPerPage),
            stats: {
                ...this.state.stats,
                filtered: filtered.length
            }
        });
    }

    clearFilters = () => {
        this.setState({
            filters: {
                famille: '',
                nomCommercial: '',
                dci: '',
                dosage: '',
                forme: '',
                conditionnement: '',
                search: '',
                reference: ''
            },
            currentPage: 1
        }, () => {
            this.applyFilters();
        });
    }

    getCurrentPageData = () => {
        const { filteredProducts, currentPage, itemsPerPage } = this.state;
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredProducts.slice(startIndex, startIndex + itemsPerPage);
    }

    handlePageChange = (pageNumber) => {
        this.setState({ currentPage: pageNumber });
    }

    handleItemsPerPageChange = (newItemsPerPage) => {
        this.setState({
            itemsPerPage: newItemsPerPage,
            currentPage: 1,
            totalPages: Math.ceil(this.state.filteredProducts.length / newItemsPerPage)
        });
    }

    exportToCSV = () => {
        const { filteredProducts } = this.state;

        const csvData = filteredProducts.map(product => ({
            'ID': product._id || product.id || '',
            'Nom': product.nom || '',
            'Nom Commercial': product.nomCommercial || '',
            'DCI': product.DCI || product.dci || '',
            'Dosage': product.dosage || '',
            'Forme': product.forme || '',
            'Conditionnement': product.conditionnement || '',
            'Famille': product.famille || '',
            'Laboratoire': product.laboratoire || product.fabricant || '',
            'Code': product.code || '',
            'Prix': product.prix || '',
            'Date Création': product.createdAt || '',
            'Date Mise à jour': product.updatedAt || ''
        }));

        const csv = Papa.unparse(csvData, {
            delimiter: ";",
            encoding: "UTF-8"
        });

        const blob = new Blob(["\ufeff", csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `medicaments_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (this.props.showNotification) {
            this.props.showNotification(`${filteredProducts.length} médicaments exportés avec succès`, 'success');
        }
    }

    openModal = (type, product = null) => {
        this.setState({
            showModal: true,
            modalType: type,
            selectedProduct: product,
            editForm: product ? {
                nom: product.nom || '',
                nomCommercial: product.nomCommercial || '',
                DCI: product.DCI || product.dci || '',
                dosage: product.dosage || '',
                forme: product.forme || '',
                conditionnement: product.conditionnement || '',
                famille: product.famille || '',
                laboratoire: product.laboratoire || product.fabricant || '',
                prix: product.prix || '',
                code: product.code || '',
                isRef: product.isRef || 0,
                referenceProductId: product.referenceProductId || ''
            } : {
                nom: '',
                nomCommercial: '',
                DCI: '',
                dosage: '',
                forme: '',
                conditionnement: '',
                famille: '',
                laboratoire: '',
                prix: '',
                code: '',
                isRef: 0,
                referenceProductId: ''
            }
        });
    }

    closeModal = () => {
        this.setState({
            showModal: false,
            modalType: 'view',
            selectedProduct: null,
            editForm: {
                nom: '',
                nomCommercial: '',
                DCI: '',
                dosage: '',
                forme: '',
                conditionnement: '',
                famille: '',
                laboratoire: '',
                prix: '',
                code: '',
                isRef: 0,
                referenceProductId: ''
            }
        });
    }

    handleFormChange = (field, value) => {
        this.setState({
            editForm: {
                ...this.state.editForm,
                [field]: value
            }
        });
    }

    handleSave = () => {
        const { modalType, editForm } = this.state;

        // Validation basique
        if (!editForm.nomCommercial || !editForm.DCI) {
            if (this.props.showNotification) {
                this.props.showNotification('Veuillez remplir les champs obligatoires', 'warning');
            }
            return;
        }

        if (modalType === 'add') {
            // Simulation d'ajout
            const newProduct = {
                ...editForm,
                _id: Date.now().toString(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            this.setState(prevState => ({
                products: [...prevState.products, newProduct],
                filteredProducts: [...prevState.filteredProducts, newProduct],
                stats: {
                    total: prevState.stats.total + 1,
                    filtered: prevState.stats.filtered + 1
                }
            }), () => {
                this.applyFilters();
            });

            if (this.props.showNotification) {
                this.props.showNotification('Médicament ajouté avec succès', 'success');
            }
        } else if (modalType === 'edit') {
            // Simulation de modification
            this.setState(prevState => ({
                products: prevState.products.map(p =>
                    p._id === prevState.selectedProduct._id
                        ? { ...p, ...editForm, updatedAt: new Date().toISOString() }
                        : p
                )
            }), () => {
                this.applyFilters();
            });

            if (this.props.showNotification) {
                this.props.showNotification('Médicament modifié avec succès', 'success');
            }
        }

        this.closeModal();
    }

    handleDelete = (product) => {
        if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${product.nomCommercial || product.nom}" ?`)) {
            this.setState(prevState => ({
                products: prevState.products.filter(p => p._id !== product._id)
            }), () => {
                this.applyFilters();
                if (this.props.showNotification) {
                    this.props.showNotification('Médicament supprimé avec succès', 'success');
                }
            });
        }
    }

    renderFilters() {
        const { filters } = this.state;
        const hasActiveFilters = Object.values(filters).some(value => value !== '');

        return (
            <div className="filter-section">
                <div className="filter-header">
                    <h4><Glyphicon glyph="filter" /> Filtres de recherche</h4>
                    {hasActiveFilters && (
                        <Button
                            bsStyle="link"
                            onClick={this.clearFilters}
                            className="clear-filters-btn"
                        >
                            <Glyphicon glyph="remove" /> Effacer les filtres
                        </Button>
                    )}
                </div>

                <Row className="filter-grid">
                    <Col md={12}>
                        <InputGroup className="search-box">
                            <InputGroup.Addon>
                                <Glyphicon glyph="search" />
                            </InputGroup.Addon>
                            <FormControl
                                type="text"
                                placeholder="Recherche globale : nom, DCI, laboratoire, famille..."
                                value={filters.search}
                                onChange={(e) => this.handleFilterChange('search', e.target.value)}
                                className="search-input"
                            />
                        </InputGroup>
                    </Col>
                </Row>

                <Row className="quick-filters">
                    <Col md={2}>
                        <FormControl
                            type="text"
                            placeholder="Filtrer par famille (ex: Antibiotique)"
                            value={filters.famille}
                            onChange={(e) => this.handleFilterChange('famille', e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <FormControl
                            type="text"
                            placeholder="Nom commercial (ex: Doliprane)"
                            value={filters.nomCommercial}
                            onChange={(e) => this.handleFilterChange('nomCommercial', e.target.value)}
                        />
                    </Col>
                    <Col md={2}>
                        <FormControl
                            type="text"
                            placeholder="DCI / Principe actif (ex: Paracétamol)"
                            value={filters.dci}
                            onChange={(e) => this.handleFilterChange('DCI', e.target.value)}
                        />
                    </Col>
                    <Col md={2}>
                        <FormControl
                            type="text"
                            placeholder="Forme galénique (ex: Comprimé)"
                            value={filters.forme}
                            onChange={(e) => this.handleFilterChange('forme', e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <DropdownButton
                            title={
                                filters.reference === 'referenced' ? '📋 Produits Référencés' :
                                filters.reference === 'non-referenced' ? '📝 Produits Non Référencés' :
                                '🔄 Filtrer par Référence'
                            }
                            id="reference-filter"
                            bsStyle={
                                filters.reference === 'referenced' ? 'success' :
                                filters.reference === 'non-referenced' ? 'warning' :
                                'default'
                            }
                            block
                        >
                            <MenuItem
                                onSelect={() => this.handleFilterChange('reference', '')}
                                active={filters.reference === ''}
                            >
                                <Glyphicon glyph="th-list" /> Tous les produits
                            </MenuItem>
                            <MenuItem divider />
                            <MenuItem
                                onSelect={() => this.handleFilterChange('reference', 'referenced')}
                                active={filters.reference === 'referenced'}
                            >
                                <Glyphicon glyph="ok-circle" style={{ color: '#28a745' }} /> Produits Référencés
                            </MenuItem>
                            <MenuItem
                                onSelect={() => this.handleFilterChange('reference', 'non-referenced')}
                                active={filters.reference === 'non-referenced'}
                            >
                                <Glyphicon glyph="exclamation-sign" style={{ color: '#ffc107' }} /> Produits Non Référencés
                            </MenuItem>
                        </DropdownButton>
                    </Col>
                </Row>
            </div>
        );
    }

    renderStats() {
        const { stats, filteredProducts } = this.state;

        return (
            <div className="stats-section">
                <Row>
                    <Col md={3}>
                        <div className="stat-card total">
                            <Glyphicon glyph="list-alt" />
                            <div className="stat-content">
                                <div className="stat-value">{stats.total}</div>
                                <div className="stat-label">Total Médicaments</div>
                            </div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="stat-card filtered">
                            <Glyphicon glyph="filter" />
                            <div className="stat-content">
                                <div className="stat-value">{stats.filtered}</div>
                                <div className="stat-label">Filtrés</div>
                            </div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="stat-card pages">
                            <Glyphicon glyph="file" />
                            <div className="stat-content">
                                <div className="stat-value">{this.state.totalPages}</div>
                                <div className="stat-label">Pages</div>
                            </div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="stat-card export">
                            <Glyphicon glyph="download" />
                            <div className="stat-content">
                                <Button
                                    bsStyle="success"
                                    onClick={this.exportToCSV}
                                    block
                                >
                                    Exporter {filteredProducts.length} éléments
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }

    renderTable() {
        const { loading, filteredProducts } = this.state;
        const currentData = this.getCurrentPageData();

        if (loading) {
            return (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <h4>Chargement de la base de données...</h4>
                    <p>Veuillez patienter pendant le chargement des données</p>
                </div>
            );
        }

        if (filteredProducts.length === 0) {
            const { error } = this.state;
            return (
                <div className="empty-state">
                    <Glyphicon glyph="inbox" className="empty-icon" />
                    <h4>{error ? 'Impossible de charger les médicaments' : 'Aucun médicament trouvé'}</h4>
                    <p>{error || 'Aucune donnée disponible dans la base locale ou sur le serveur.'}</p>
                    <Button bsStyle="primary" onClick={error ? this.loadProducts : this.clearFilters}>
                        {error ? 'Réessayer' : 'Réinitialiser les filtres'}
                    </Button>
                </div>
            );
        }

        return (
            <div className="table-container">
                <Table responsive hover className="data-table">
                    <thead>
                    <tr>
                        <th className="sortable" onClick={() => this.handleSort('nomCommercial')}>
                            Nom Commercial {this.getSortIndicator('nomCommercial')}
                        </th>
                        <th className="sortable" onClick={() => this.handleSort('DCI')}>
                            DCI {this.getSortIndicator('DCI')}
                        </th>
                        <th className="sortable" onClick={() => this.handleSort('dosage')}>
                            Dosage {this.getSortIndicator('dosage')}
                        </th>
                        <th>Forme</th>
                        <th>Conditionnement</th>
                        <th className="sortable" onClick={() => this.handleSort('isRef')}>
                            Référence {this.getSortIndicator('isRef')}
                        </th>
                        <th className="actions-header">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentData.map((product, index) => (
                        <tr key={product._id || product.id || index} className="table-row">
                            <td className="product-name">
                                <strong>{product.nomCommercial || product.nom || 'Sans nom'}</strong>
                                {product.laboratoire && (
                                    <div className="laboratoire">{product.laboratoire}</div>
                                )}
                            </td>
                            <td>
                                <span className="dci-text">{product.DCI || product.dci || '-'}</span>
                            </td>
                            <td>
                                <span className="dosage-text">{product.dosage || '-'}</span>
                            </td>
                            <td>
                                <span className="forme-label">{product.forme || '-'}</span>
                            </td>
                            <td>
                                <span className="conditionnement-text">{product.conditionnement || '-'}</span>
                            </td>
                            <td>
                                {product.isRef === 1 ? (
                                    <Badge className="reference-badge reference-yes">
                                        <Glyphicon glyph="ok-circle" /> Référencé
                                    </Badge>
                                ) : (
                                    <Badge className="reference-badge reference-no">
                                        <Glyphicon glyph="exclamation-sign" /> Non Référencé
                                        {product.referenceProductId && (
                                            <div className="reference-id">
                                                <small>Réf ID: {product.referenceProductId}</small>
                                            </div>
                                        )}
                                    </Badge>
                                )}
                            </td>
                            <td className="actions-cell">
                                <Button
                                    bsStyle="info"
                                    bsSize="xs"
                                    onClick={() => this.openModal('view', product)}
                                    title="Voir détails"
                                    className="action-btn view-btn"
                                >
                                    <Glyphicon glyph="eye-open" />
                                    <span className="btn-label">Détails</span>
                                </Button>
                                <Button
                                    bsStyle="warning"
                                    bsSize="xs"
                                    onClick={() => this.openModal('edit', product)}
                                    title="Modifier"
                                    className="action-btn edit-btn"
                                >
                                    <Glyphicon glyph="edit" />
                                    <span className="btn-label">Modifier</span>
                                </Button>
                                <Button
                                    bsStyle="danger"
                                    bsSize="xs"
                                    onClick={() => this.handleDelete(product)}
                                    title="Supprimer"
                                    className="action-btn delete-btn"
                                >
                                    <Glyphicon glyph="trash" />
                                    <span className="btn-label">Supprimer</span>
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </div>
        );
    }

    renderPagination() {
        const { currentPage, totalPages, filteredProducts, itemsPerPage, itemsPerPageOptions } = this.state;

        if (filteredProducts.length === 0) return null;

        const startItem = ((currentPage - 1) * itemsPerPage) + 1;
        const endItem = Math.min(currentPage * itemsPerPage, filteredProducts.length);

        return (
            <div className="pagination-section">
                <Row>
                    <Col md={4} className="pagination-info-col">
                        <div className="pagination-info">
                            <Glyphicon glyph="list-alt" className="pagination-icon" />
                            Affichage de <strong>{startItem}</strong> à <strong>{endItem}</strong> sur <strong>{filteredProducts.length}</strong> éléments
                        </div>
                    </Col>
                    <Col md={4} className="items-per-page-col">
                        <div className="items-per-page-selector">
                            <span className="per-page-label">
                                <Glyphicon glyph="resize-horizontal" /> Éléments par page:
                            </span>
                            <DropdownButton
                                title={itemsPerPage}
                                id="items-per-page-dropdown"
                                bsSize="small"
                                className="items-per-page-dropdown"
                            >
                                {itemsPerPageOptions.map(option => (
                                    <MenuItem
                                        key={option}
                                        onSelect={() => this.handleItemsPerPageChange(option)}
                                        active={itemsPerPage === option}
                                    >
                                        {option} éléments
                                    </MenuItem>
                                ))}
                            </DropdownButton>
                        </div>
                    </Col>
                    <Col md={4} className="pagination-controls-col">
                        {totalPages > 1 && (
                            <div className="pagination-controls">
                                <div className="pagination-navigation">
                                    <Button
                                        bsSize="small"
                                        onClick={() => this.handlePageChange(1)}
                                        disabled={currentPage === 1}
                                        className="nav-btn first-btn"
                                        title="Première page"
                                    >
                                        <Glyphicon glyph="fast-backward" />
                                    </Button>
                                    <Button
                                        bsSize="small"
                                        onClick={() => this.handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="nav-btn prev-btn"
                                        title="Page précédente"
                                    >
                                        <Glyphicon glyph="step-backward" />
                                    </Button>

                                    <span className="page-indicator">
                                        Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
                                    </span>

                                    <Button
                                        bsSize="small"
                                        onClick={() => this.handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="nav-btn next-btn"
                                        title="Page suivante"
                                    >
                                        <Glyphicon glyph="step-forward" />
                                    </Button>
                                    <Button
                                        bsSize="small"
                                        onClick={() => this.handlePageChange(totalPages)}
                                        disabled={currentPage === totalPages}
                                        className="nav-btn last-btn"
                                        title="Dernière page"
                                    >
                                        <Glyphicon glyph="fast-forward" />
                                    </Button>
                                </div>

                                {totalPages > 5 && (
                                    <div className="pagination-classic">
                                        <Pagination
                                            prev
                                            next
                                            first
                                            last
                                            ellipsis
                                            boundaryLinks
                                            items={totalPages}
                                            maxButtons={5}
                                            activePage={currentPage}
                                            onSelect={this.handlePageChange}
                                            className="custom-pagination"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        );
    }

    render() {
        const { showModal, modalType, selectedProduct, editForm } = this.state;

        return (
            <div className="content database-management">
                <Grid fluid>
                    <Row>
                        <Col md={12}>
                            <Card
                                content={
                                    <div className="database-content">
                                        {this.renderStats()}
                                        {this.renderFilters()}

                                        <div className="action-buttons-section">
                                            <Button
                                                bsStyle="primary"
                                                onClick={() => this.openModal('add')}
                                                className="add-btn"
                                            >
                                                <Glyphicon glyph="plus" /> Ajouter un médicament
                                            </Button>
                                            <Button
                                                bsStyle="default"
                                                onClick={this.loadProducts}
                                                className="refresh-btn"
                                            >
                                                <Glyphicon glyph="refresh" /> Actualiser
                                            </Button>
                                        </div>

                                        {this.renderTable()}
                                        {this.renderPagination()}
                                    </div>
                                }
                            />
                        </Col>
                    </Row>
                </Grid>

                {/* Modal */}
                <Modal
                    show={showModal}
                    onHide={this.closeModal}
                    bsSize="large"
                    className="database-modal"
                >
                    <Modal.Header closeButton className="modal-header">
                        <Modal.Title>
                            <Glyphicon glyph={
                                modalType === 'view' ? 'eye-open' :
                                    modalType === 'add' ? 'plus' : 'edit'
                            } />
                            {' '}
                            {modalType === 'view' && 'Détails du Médicament'}
                            {modalType === 'add' && 'Nouveau Médicament'}
                            {modalType === 'edit' && 'Modifier le Médicament'}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body className="modal-body">
                        {modalType === 'view' && selectedProduct && (
                            <div className="product-detail-view">
                                <Row>
                                    <Col md={6}>
                                        <div className="detail-section">
                                            <h5><Glyphicon glyph="tag" /> Informations générales</h5>
                                            <div className="detail-item">
                                                <label>Nom Commercial:</label>
                                                <div className="detail-value">{selectedProduct.nomCommercial || selectedProduct.nom || '-'}</div>
                                            </div>
                                            <div className="detail-item">
                                                <label>DCI:</label>
                                                <div className="detail-value">{selectedProduct.DCI || selectedProduct.dci || '-'}</div>
                                            </div>
                                            <div className="detail-item">
                                                <label>Dosage:</label>
                                                <div className="detail-value">{selectedProduct.dosage || '-'}</div>
                                            </div>
                                            <div className="detail-item">
                                                <label>Forme:</label>
                                                <div className="detail-value">{selectedProduct.forme || '-'}</div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="detail-section">
                                            <h5><Glyphicon glyph="info-sign" /> Détails supplémentaires</h5>
                                            <div className="detail-item">
                                                <label>Conditionnement:</label>
                                                <div className="detail-value">{selectedProduct.conditionnement || '-'}</div>
                                            </div>
                                            <div className="detail-item">
                                                <label>Famille:</label>
                                                <div className="detail-value">{selectedProduct.famille || '-'}</div>
                                            </div>
                                            <div className="detail-item">
                                                <label>Laboratoire:</label>
                                                <div className="detail-value">{selectedProduct.laboratoire || selectedProduct.fabricant || '-'}</div>
                                            </div>
                                            <div className="detail-item">
                                                <label>Code:</label>
                                                <div className="detail-value">
                                                    <Badge>{selectedProduct.code || '-'}</Badge>
                                                </div>
                                            </div>
                                            <div className="detail-item">
                                                <label>Statut Référence:</label>
                                                <div className="detail-value">
                                                    {selectedProduct.isRef === 1 ? (
                                                        <Badge className="reference-badge reference-yes">
                                                            <Glyphicon glyph="ok-circle" /> Produit Référencé
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="reference-badge reference-no">
                                                            <Glyphicon glyph="exclamation-sign" /> Produit Non Référencé
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            {selectedProduct.referenceProductId && (
                                                <div className="detail-item">
                                                    <label>ID du Produit de Référence:</label>
                                                    <div className="detail-value">
                                                        <Badge bsStyle="info">{selectedProduct.referenceProductId}</Badge>
                                                    </div>
                                                </div>
                                            )}
                                            {selectedProduct.prix && (
                                                <div className="detail-item">
                                                    <label>Prix:</label>
                                                    <div className="detail-value prix">
                                                        {selectedProduct.prix} MAD
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        {(modalType === 'add' || modalType === 'edit') && (
                            <Form className="modal-form">
                                <Row>
                                    <Col md={6}>
                                        <FormGroup controlId="nomCommercial">
                                            <ControlLabel>Nom Commercial *</ControlLabel>
                                            <FormControl
                                                type="text"
                                                value={editForm.nomCommercial}
                                                onChange={(e) => this.handleFormChange('nomCommercial', e.target.value)}
                                                placeholder="Ex: Doliprane"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup controlId="dci">
                                            <ControlLabel>DCI (Dénomination Commune Internationale) *</ControlLabel>
                                            <FormControl
                                                type="text"
                                                value={editForm.DCI}
                                                onChange={(e) => this.handleFormChange('DCI', e.target.value)}
                                                placeholder="Ex: Paracétamol"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={4}>
                                        <FormGroup controlId="dosage">
                                            <ControlLabel>Dosage</ControlLabel>
                                            <FormControl
                                                type="text"
                                                value={editForm.dosage}
                                                onChange={(e) => this.handleFormChange('dosage', e.target.value)}
                                                placeholder="Ex: 500mg"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup controlId="forme">
                                            <ControlLabel>Forme</ControlLabel>
                                            <FormControl
                                                type="text"
                                                value={editForm.forme}
                                                onChange={(e) => this.handleFormChange('forme', e.target.value)}
                                                placeholder="Ex: Comprimé"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup controlId="conditionnement">
                                            <ControlLabel>Conditionnement</ControlLabel>
                                            <FormControl
                                                type="text"
                                                value={editForm.conditionnement}
                                                onChange={(e) => this.handleFormChange('conditionnement', e.target.value)}
                                                placeholder="Ex: Boîte de 16"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup controlId="famille">
                                            <ControlLabel>Famille thérapeutique</ControlLabel>
                                            <FormControl
                                                type="text"
                                                value={editForm.famille}
                                                onChange={(e) => this.handleFormChange('famille', e.target.value)}
                                                placeholder="Ex: Antalgique"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup controlId="laboratoire">
                                            <ControlLabel>Laboratoire</ControlLabel>
                                            <FormControl
                                                type="text"
                                                value={editForm.laboratoire}
                                                onChange={(e) => this.handleFormChange('laboratoire', e.target.value)}
                                                placeholder="Ex: Sanofi"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={4}>
                                        <FormGroup controlId="prix">
                                            <ControlLabel>Prix (MAD)</ControlLabel>
                                            <FormControl
                                                type="number"
                                                value={editForm.prix}
                                                onChange={(e) => this.handleFormChange('prix', e.target.value)}
                                                placeholder="0.00"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup controlId="code">
                                            <ControlLabel>Code produit</ControlLabel>
                                            <FormControl
                                                type="text"
                                                value={editForm.code}
                                                onChange={(e) => this.handleFormChange('code', e.target.value)}
                                                placeholder="Code unique"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup controlId="nom">
                                            <ControlLabel>Nom générique</ControlLabel>
                                            <FormControl
                                                type="text"
                                                value={editForm.nom}
                                                onChange={(e) => this.handleFormChange('nom', e.target.value)}
                                                placeholder="Nom alternatif"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup controlId="isRef">
                                            <ControlLabel>Ce produit est-il un produit de référence ?</ControlLabel>
                                            <FormControl
                                                componentClass="select"
                                                value={editForm.isRef}
                                                onChange={(e) => this.handleFormChange('isRef', parseInt(e.target.value))}
                                            >
                                                <option value={0}>Non - Produit Non Référencé</option>
                                                <option value={1}>Oui - Produit Référencé</option>
                                            </FormControl>
                                        </FormGroup>
                                    </Col>
                                    {editForm.isRef === 0 && (
                                        <Col md={6}>
                                            <FormGroup controlId="referenceProductId">
                                                <ControlLabel>ID du produit de référence</ControlLabel>
                                                <FormControl
                                                    type="text"
                                                    value={editForm.referenceProductId}
                                                    onChange={(e) => this.handleFormChange('referenceProductId', e.target.value)}
                                                    placeholder="ID du produit référence"
                                                />
                                                <small className="help-block">
                                                    Saisissez l'ID du produit qui sert de référence à ce produit
                                                </small>
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>

                                <div className="form-note">
                                    <Glyphicon glyph="info-sign" /> Les champs marqués d'un * sont obligatoires
                                </div>
                            </Form>
                        )}
                    </Modal.Body>

                    <Modal.Footer className="modal-footer">
                        <Button onClick={this.closeModal} className="cancel-btn">
                            Annuler
                        </Button>
                        {(modalType === 'add' || modalType === 'edit') && (
                            <Button
                                bsStyle="primary"
                                onClick={this.handleSave}
                                className="save-btn"
                            >
                                <Glyphicon glyph={
                                    modalType === 'add' ? 'ok' : 'saved'
                                } />
                                {' '}
                                {modalType === 'add' ? 'Ajouter' : 'Enregistrer'}
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

DatabaseManagement.propTypes = {
    products: PropTypes.array,
    fetchAllMongoProducts: PropTypes.func.isRequired,
    showNotification: PropTypes.func
};

const mapStateToProps = state => ({
    products: state.products.products || []
});

const mapDispatchToProps = dispatch => ({
    fetchAllMongoProducts: () => dispatch(fetchAllMongoProducts())
});

export default connect(mapStateToProps, mapDispatchToProps)(DatabaseManagement);